import React from "react";
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import FormControl from "@material-ui/core/FormControl";
import { makeStyles } from "@material-ui/core/styles";
import MenuItem from "@material-ui/core/MenuItem";
import { TwilioEntryConfiguration } from "../models/Subscription";
import TextField from "@material-ui/core/TextField";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Switch from "@material-ui/core/Switch";
import FormLabel from "@material-ui/core/FormLabel";
import Fab from "@material-ui/core/Fab";
import DeleteIcon from "@material-ui/icons/Delete";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { pascalToTitleCase } from "../utils";
import colors from "../constants/colors";

export interface EditSubscriptionAdditionalConfigurationItemProps {
	// configuration item
	config: TwilioEntryConfiguration;
	// delete handler
	onRemove: (id: string) => void;
	// update handler
	onUpdate: (updatedConfig: TwilioEntryConfiguration) => void;
	// environment to choose from in edit form
	environments: { val: number; text: string }[];
}

const useStyles = makeStyles((theme) => ({
	container: {
		display: "flex",
		flexWrap: "wrap",
		maxWidth: 996,
		marginBottom: theme.spacing(4),
		padding: theme.spacing(3, 3, 0, 3),
		backgroundColor: theme.palette.background.paper,
		boxShadow: theme.shadows[1],
		position: "relative",
	},
	formControl: {
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
		marginBottom: theme.spacing(2),
	},
	formControlLabel: {
		fontSize: theme.typography.caption.fontSize,
		margin: 0,
		textTransform: "uppercase",
		color: colors.gray600,
	},
	formGroup: {
		width: "100%",
	},
	inputField: {
		width: 300,
		marginTop: theme.spacing(1),
	},
	deleteButton: {
		position: "absolute",
		bottom: theme.spacing(3),
		right: theme.spacing(3),
	},
}));

/**
 * Edit form for subscription additional configuration
 * @param props
 */
export const EditSubscriptionAdditionalConfigurationItem = (
	props: EditSubscriptionAdditionalConfigurationItemProps,
) => {
	const classes = useStyles();

	const [showPassword, setShowPassword] = React.useState(false);

	const update = (prop: string, val: any) => props.onUpdate({ ...props.config, [prop]: val });

	return (
		<Box className={classes.container}>
			<FormGroup row className={classes.formGroup}>
				<FormControl className={classes.formControl}>
					<FormLabel className={classes.formControlLabel}>Environment</FormLabel>
					<TextField
						select
						className={classes.inputField}
						value={props.config.Environment}
						onChange={(e) => update("Environment", Number(e.target.value))}
						margin="normal"
					>
						{props.environments.map((option) => (
							<MenuItem key={option.val} value={option.val}>
								{option.text}
							</MenuItem>
						))}
					</TextField>
				</FormControl>
				<FormControl className={classes.formControl}>
					<FormLabel className={classes.formControlLabel}>Account SID</FormLabel>
					<TextField
						className={classes.inputField}
						value={props.config.AccountSid}
						margin="normal"
						onChange={(e) => update("AccountSid", e.target.value)}
					/>
				</FormControl>
			</FormGroup>
			<FormGroup row className={classes.formGroup}>
				<FormControl className={classes.formControl}>
					<FormLabel className={classes.formControlLabel}>Api Key Sid</FormLabel>
					<TextField
						className={classes.inputField}
						value={props.config.TwilioApiKeySid}
						margin="normal"
						onChange={(e) => update("TwilioApiKeySid", e.target.value)}
					/>
				</FormControl>
				<FormControl className={classes.formControl}>
					<FormLabel className={classes.formControlLabel}>Api Key Secret</FormLabel>
					<div>
						<TextField
							type={showPassword ? "text" : "password"}
							className={classes.inputField}
							value={props.config.TwilioApiKeySecret}
							margin="normal"
							onChange={(e) => update("TwilioApiKeySecret", e.target.value)}
						/>
						<IconButton aria-label="toggle password visibility" onClick={() => setShowPassword(!showPassword)}>
							{showPassword ? <Visibility /> : <VisibilityOff />}
						</IconButton>
					</div>
				</FormControl>
			</FormGroup>
			<FormGroup row className={classes.formGroup}>
				<FormControl className={classes.formControl}>
					<FormLabel className={classes.formControlLabel}>Project Number</FormLabel>
					<TextField
						className={classes.inputField}
						value={props.config.ProjectNumber}
						margin="normal"
						onChange={(e) => update("ProjectNumber", e.target.value)}
					/>
				</FormControl>
				<FormControl className={classes.formControl}>
					<FormLabel className={classes.formControlLabel}>Enable remote log level override</FormLabel>
					<FormGroup row>
						<Switch
							color="primary"
							checked={props.config.IsLogLevelOverrideEnabled}
							onChange={(e) => update("IsLogLevelOverrideEnabled", e.target.checked)}
							value={props.config.IsLogLevelOverrideEnabled}
						/>
					</FormGroup>
				</FormControl>
			</FormGroup>
			<FormGroup row className={classes.formGroup}>
				<FormControl className={classes.formControl}>
					<FormLabel className={classes.formControlLabel}>Enable remote fetching of Key and Secret values</FormLabel>
					<FormGroup row>
						<Switch
							color="primary"
							checked={props.config.EnableApiSecretRemoteFetch}
							onChange={(e) => update("EnableApiSecretRemoteFetch", e.target.checked)}
							value={props.config.EnableApiSecretRemoteFetch}
						/>
					</FormGroup>
				</FormControl>
			</FormGroup>
			<FormGroup row className={classes.formGroup}>
				<FormControl className={classes.formControl}>
					<FormLabel className={classes.formControlLabel}>Logging Level</FormLabel>
					<FormGroup row>
						{Object.keys(props.config.TwilioLoggingConfiguration).map((k) => (
							<FormControlLabel
								key={k}
								control={
									<Checkbox
										color="primary"
										checked={props.config.TwilioLoggingConfiguration[k]}
										onChange={(e) =>
											update("TwilioLoggingConfiguration", {
												...props.config.TwilioLoggingConfiguration,
												[k]: e.target.checked,
											})
										}
										value={props.config.TwilioLoggingConfiguration[k]}
									/>
								}
								label={pascalToTitleCase(k)}
							/>
						))}
					</FormGroup>
				</FormControl>
			</FormGroup>
			<Fab
				color="secondary"
				size="small"
				aria-label="delete"
				className={classes.deleteButton}
				onClick={() => props.onRemove(props.config._id!)}
			>
				<DeleteIcon />
			</Fab>
		</Box>
	);
};
